import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { getCarrierDisplayName } from '../../configs';
import { Carrier, CarrierTypes } from '@app/services/carriers/carrier.model';
import { Store } from '@ngrx/store';
import * as fromCarriers from '@app/pages/delivery-service/store/reducers/index';
import { IShopMetadata } from '@pdqdev/pdq-shared/dist/interfaces/IShopMetadata';
import { CarrierTypeNames, masterCarrierTypeNames } from '@pdqdev/pdq-shared/dist/CarrierData';
import { ShopService } from '@app/services/shops/shop.service';
import { CountryCode, getCountryDisplayNameByCountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Subscription } from 'rxjs';

export const supportedCountries = [CountryCode.IL, CountryCode.US, CountryCode.CA, CountryCode.UK];

@Component({
  selector: 'app-choose-carrier',
  templateUrl: './choose-carrier.component.html',
  styleUrls: ['./choose-carrier.component.scss'],
  standalone: false,
})
export class ChooseCarrierComponent implements OnInit, OnDestroy {
  @Output() onOpenCarrier = new EventEmitter<CarrierTypes>();
  originalCarrierTypes = [];
  allCarrierTypes$ = this.store.select(fromCarriers.selectCarrierTypes);
  carriersList = [];
  searchCarrier: string;
  filteredCarrierList = [];
  countryCode: CountryCode;
  shopMetadata: IShopMetadata;
  originCountryCC: CountryCode = null;
  destinationCountryCC: CountryCode = null;
  countryCodeOptions: string[] = [];
  emptyCarrierString: string = 'No delivery services found\n' + 'maybe try something else?';
  countryOptions: { label: string; value: string }[] = [];
  shopSubscription: Subscription;
  constructor(
    public router: Router,
    private store: Store<fromCarriers.State>,
    private _shopService: ShopService,
  ) {}
  ngOnDestroy(): void {
    if (this.shopSubscription) {
      this.shopSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.shopSubscription = this._shopService.shop$.subscribe((shop) => {
      this.countryCode = shop?.address?.countryCode as CountryCode;
      this.destinationCountryCC = this.countryCode;
      this.originCountryCC = this.countryCode;
      this.loadCarrierTypes();
    });

    this.initCountryOptions();
  }

  initCountryOptions() {
    this.countryOptions = supportedCountries.map((country: CountryCode) => {
      return {
        value: country,
        label: this.countryCodeDisplayName(country),
      };
    });
  }

  loadCarrierTypes(): void {
    const exludedCarrierTypes = [CarrierTypeNames.DEFAULT, ...masterCarrierTypeNames];
    this.allCarrierTypes$.subscribe((carrierTypes) => {
      if (!carrierTypes) {
        return;
      }

      carrierTypes = carrierTypes?.filter((carrierType) => {
        return !exludedCarrierTypes.includes(carrierType?.name);
      });

      this.originalCarrierTypes = carrierTypes;

      this.carriersList = carrierTypes
        ?.map((carrier) => {
          if (carrier.custom_fields.shipping?.countries[this.countryCode]?.includes(this.countryCode)) {
            return carrier;
          }
        })
        .filter(Boolean);

      this.carriersList = this.sortCarriers(this.carriersList);
      this.filteredCarrierList = this.carriersList;
    });
  }

  onCountryChange(selectedCountry, shipping?: 'origin' | 'destination') {
    let filteredOriginalCarrierTypes = [];
    switch (shipping) {
      case 'destination': {
        this.destinationCountryCC = selectedCountry?.value;
        break;
      }
      case 'origin': {
        this.originCountryCC = selectedCountry?.value;
        break;
      }
    }

    if (this.destinationCountryCC && this.originCountryCC) {
      filteredOriginalCarrierTypes = this.originalCarrierTypes
        .map((carrier) => {
          if (carrier.custom_fields.shipping?.countries[this.originCountryCC]?.includes(this.destinationCountryCC))
            return carrier;
        })
        .filter(Boolean);
    }

    filteredOriginalCarrierTypes = this.sortCarriers(filteredOriginalCarrierTypes);
    this.carriersList = filteredOriginalCarrierTypes;
    this.filteredCarrierList = filteredOriginalCarrierTypes;
  }

  openCarrierForm(carrierType: CarrierTypes): void {
    this.onOpenCarrier.emit(carrierType);
  }

  getDisplayName(carrierTypeName: CarrierTypeNames): string {
    return getCarrierDisplayName(carrierTypeName);
  }

  onCarrierSearch(event: any): void {
    const searchedCarrierType = event.target?.value?.toLowerCase();
    const filteredCarriers = [];

    if (event) {
      this.carriersList.forEach((carrier: Carrier) => {
        const carrierType = carrier.name?.toLowerCase();

        if (carrierType.includes(searchedCarrierType)) {
          filteredCarriers.push(carrier);
        }
      });
    }
    this.filteredCarrierList = filteredCarriers;
  }

  countryCodeDisplayName(country: CountryCode): string {
    if (!country) {
      return;
    }
    return getCountryDisplayNameByCountryCode(country);
  }

  sortCarriers(carrierList: CarrierTypes[]) {
    return carrierList.sort((a, b) => {
      if (b.name.includes(CarrierTypeNames.SELF)) return 1;
    });
  }
}
